import { EventType, type MatrixClient, MsgType, RelationType } from 'matrix-js-sdk'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { enrichMatrixMessage } from '@/modules/chat-matrix-fast/services/matrixProfileCache'
import { hashToNumber } from '@/modules/chat-matrix-fast/services/matrixEventMapper'
import { isAllowedChatUploadFile } from '@/modules/chat-matrix-fast/utils/fileUtils'
import { createVideoPosterFile } from '@/modules/chat-matrix-fast/utils/videoPoster'
import {
  hasMarkdownMarkup,
  markdownToMatrixFormattedBody,
} from '@/modules/chat-matrix-fast/utils/messageRichText'
import { enrichMessagesWithReadReceipts } from '@/modules/chat-matrix-fast/services/matrixReadReceiptService'
import { sendMatrixTextEvent } from '@/modules/chat-matrix-fast/services/matrixTextDelivery'
import {
  getMatrixHttpStatus,
  withMatrixAuthRetry,
} from '@/modules/chat-matrix-fast/services/matrixAuthRecovery'
import {
  onMatrixClientReset,
  peekMatrixClient,
  startMatrixClient,
} from '@/modules/chat-matrix-fast/services/matrixClientLifecycle'

let mediaUploadLimitBytes: number | null | undefined

onMatrixClientReset(() => {
  mediaUploadLimitBytes = undefined
})

function formatUploadSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 Б'
  }

  const units = ['Б', 'КБ', 'МБ', 'ГБ']
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1,
  )

  return `${Number.parseFloat((bytes / 1024 ** index).toFixed(1))} ${units[index]}`
}

function getMatrixUploadStatus(error: unknown): number | undefined {
  return getMatrixHttpStatus(error)
}

async function getMediaUploadLimit(matrixClient: MatrixClient): Promise<number | null> {
  if (mediaUploadLimitBytes !== undefined) {
    return mediaUploadLimitBytes
  }

  try {
    const config = await matrixClient.getMediaConfig()
    const uploadSize = config['m.upload.size']

    mediaUploadLimitBytes = typeof uploadSize === 'number' && uploadSize > 0
      ? uploadSize
      : null
  } catch {
    mediaUploadLimitBytes = null
  }

  return mediaUploadLimitBytes
}

async function assertFileFitsMatrixUploadLimit(
  matrixClient: MatrixClient,
  file: File,
): Promise<void> {
  const uploadLimit = await getMediaUploadLimit(matrixClient)

  if (uploadLimit && file.size > uploadLimit) {
    throw new Error(
      `validation: файл «${file.name}» слишком большой (${formatUploadSize(file.size)}). Максимальный размер для чата: ${formatUploadSize(uploadLimit)}.`,
    )
  }
}

function buildTextMessagePayload(
  content: string,
  mentionUserIds: string[],
  inReplyToEventId?: string,
): Record<string, unknown> {
  const formattedBody = markdownToMatrixFormattedBody(content)
  const useFormatted = hasMarkdownMarkup(content)
  const payload: Record<string, unknown> = {
    msgtype: MsgType.Text,
    body: content,
  }

  if (useFormatted) {
    payload.format = 'org.matrix.custom.html'
    payload.formatted_body = formattedBody
  }

  if (mentionUserIds.length) {
    payload['m.mentions'] = { user_ids: mentionUserIds }
  }

  applyInReplyToRelation(payload, inReplyToEventId)

  return payload
}

function applyInReplyToRelation(
  payload: Record<string, unknown>,
  inReplyToEventId?: string,
): void {
  if (!inReplyToEventId) {
    return
  }

  payload['m.relates_to'] = {
    'm.in_reply_to': {
      event_id: inReplyToEventId,
    },
  }
}

async function buildSentTextMessage(
  matrixClient: MatrixClient,
  roomId: string,
  content: string,
  eventId: string,
  mentionUserIds: string[],
  txnId: string,
): Promise<IMessage> {
  const mxid = matrixClient.getUserId() ?? ''
  const room = matrixClient.getRoom(roomId)

  const message: IMessage = {
    id: hashToNumber(eventId),
    matrix_event_id: eventId,
    matrix_txn_id: txnId,
    content,
    author: mxid,
    author_id: mxid,
    author_name: mxid,
    created_by: {
      id: mxid,
      full_name: mxid,
      user_name: mxid,
    },
    attachments: [],
    created_at: new Date().toISOString(),
    is_read: false,
    is_delivered: true,
    mention_user_ids: mentionUserIds.length ? mentionUserIds : undefined,
  }

  const enriched = await enrichMatrixMessage(matrixClient, message, room)
  if (!room) {
    return enriched
  }

  return enrichMessagesWithReadReceipts(matrixClient, room, [enriched])[0]
}

export async function sendMatrixMessage(
  roomId: string,
  content: string,
  options?: {
    mentionUserIds?: string[]
    clientTxnId?: string
    inReplyToEventId?: string
  },
): Promise<IMessage> {
  const mentionUserIds = options?.mentionUserIds?.filter(Boolean) ?? []
  const initialClient = await startMatrixClient()
  const txnId = options?.clientTxnId ?? initialClient.makeTxnId()
  const payload = buildTextMessagePayload(
    content,
    mentionUserIds,
    options?.inReplyToEventId,
  )

  return withMatrixAuthRetry(async () => {
    const matrixClient = peekMatrixClient() ?? await startMatrixClient()
    const response = await sendMatrixTextEvent(matrixClient, roomId, payload as any, txnId)
    return buildSentTextMessage(matrixClient, roomId, content, response.event_id, mentionUserIds, txnId)
  })
}

async function readImageDimensions(file: File): Promise<{ width?: number; height?: number }> {
  if (!file.type.startsWith('image/')) {
    return {}
  }

  try {
    const bitmap = await createImageBitmap(file)
    const result = { width: bitmap.width, height: bitmap.height }

    bitmap.close()

    return result
  } catch {
    return {}
  }
}

async function readVideoMetadata(
  file: File,
): Promise<{ w?: number; h?: number; duration?: number }> {
  if (!file.type.startsWith('video/') || typeof document === 'undefined') {
    return {}
  }

  return new Promise(resolve => {
    const video = document.createElement('video')
    const objectUrl = URL.createObjectURL(file)

    video.preload = 'metadata'
    video.onloadedmetadata = () => {
      resolve({
        w: video.videoWidth || undefined,
        h: video.videoHeight || undefined,
        duration: Number.isFinite(video.duration)
          ? Math.round(video.duration * 1000)
          : undefined,
      })
      URL.revokeObjectURL(objectUrl)
    }
    video.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({})
    }
    video.src = objectUrl
  })
}

async function sendMatrixMediaMessage(
  matrixClient: MatrixClient,
  roomId: string,
  file: File,
  caption?: string,
  inReplyToEventId?: string,
): Promise<IMessage> {
  const isVideo = file.type.startsWith('video/')
  const isImage = file.type.startsWith('image/')
  const isMedia = isVideo || isImage

  if (!isMedia && !isAllowedChatUploadFile(file)) {
    throw new Error(`Неподдерживаемый тип файла: ${file.name}`)
  }

  await assertFileFitsMatrixUploadLimit(matrixClient, file)

  let upload: Awaited<ReturnType<MatrixClient['uploadContent']>>
  try {
    upload = await matrixClient.uploadContent(file, {
      includeFilename: true,
      type: file.type || undefined,
    })
  } catch (error) {
    if (getMatrixUploadStatus(error) === 413) {
      const uploadLimit = await getMediaUploadLimit(matrixClient)
      if (uploadLimit && file.size <= uploadLimit) {
        // Portal vue nginx (same-origin /_matrix) often 413s when client_max_body_size
        // is still the default 1m — Synapse itself already allows this file.
        throw new Error(
          `validation: не удалось загрузить «${file.name}» (${formatUploadSize(file.size)}): `
          + 'прокси портала отклонил файл (HTTP 413), хотя лимит чата '
          + `${formatUploadSize(uploadLimit)}. Нужен client_max_body_size ≥ лимита на /_matrix.`,
        )
      }

      const limitText = uploadLimit
        ? ` Максимальный размер для чата: ${formatUploadSize(uploadLimit)}.`
        : ''

      throw new Error(
        `validation: файл «${file.name}» слишком большой (${formatUploadSize(file.size)}).${limitText}`,
      )
    }

    throw error
  }

  const trimmedCaption = caption?.trim()
  // body = подпись (если есть), иначе имя файла; filename всегда реальное имя
  const body = trimmedCaption || file.name
  const txnId = matrixClient.makeTxnId()

  if (!isMedia) {
    const filePayload: Record<string, unknown> = {
      msgtype: MsgType.File,
      body,
      filename: file.name,
      url: upload.content_uri,
      info: {
        mimetype: file.type || 'application/octet-stream',
        size: file.size,
      },
    }
    applyInReplyToRelation(filePayload, inReplyToEventId)

    const response = await matrixClient.sendMessage(roomId, null, filePayload as any, txnId)

    const eventId = response.event_id
    const mxid = matrixClient.getUserId() ?? ''
    const room = matrixClient.getRoom(roomId)

    const message: IMessage = {
      id: hashToNumber(eventId),
      matrix_event_id: eventId,
      matrix_txn_id: txnId,
      content: trimmedCaption || '',
      author: mxid,
      author_id: mxid,
      author_name: mxid,
      created_by: {
        id: mxid,
        full_name: mxid,
        user_name: mxid,
      },
      attachments: [{
        id: hashToNumber(eventId),
        file: upload.content_uri,
        mime_type: file.type || 'application/octet-stream',
        name: file.name,
      }],
      created_at: new Date().toISOString(),
      is_read: false,
      is_delivered: true,
    }

    const enriched = await enrichMatrixMessage(matrixClient, message, room)
    if (!room) {
      return enriched
    }

    return enrichMessagesWithReadReceipts(matrixClient, room, [enriched])[0]
  }

  const info: Record<string, unknown> = {
    mimetype: file.type,
    size: file.size,
  }

  if (isImage) {
    const { width, height } = await readImageDimensions(file)
    if (width) {
      info.w = width
    }
    if (height) {
      info.h = height
    }
  } else {
    Object.assign(info, await readVideoMetadata(file))
  }

  let thumbnailMxc: string | undefined
  if (isVideo) {
    const posterFile = await createVideoPosterFile(file)
    if (posterFile) {
      try {
        const posterUpload = await matrixClient.uploadContent(posterFile, {
          includeFilename: true,
          type: posterFile.type,
        })
        thumbnailMxc = posterUpload.content_uri
        info.thumbnail_url = thumbnailMxc
        info.thumbnail_info = {
          mimetype: posterFile.type,
          size: posterFile.size,
          w: info.w,
          h: info.h,
        }
      } catch {

      }
    }
  }

  const msgtype = isVideo ? MsgType.Video : MsgType.Image

  const mediaPayload: Record<string, unknown> = {
    msgtype,
    body,
    filename: file.name,
    url: upload.content_uri,
    info,
  }
  applyInReplyToRelation(mediaPayload, inReplyToEventId)

  const response = await matrixClient.sendMessage(roomId, null, mediaPayload as any, txnId)

  const eventId = response.event_id
  const mxid = matrixClient.getUserId() ?? ''
  const room = matrixClient.getRoom(roomId)

  const message: IMessage = {
    id: hashToNumber(eventId),
    matrix_event_id: eventId,
    matrix_txn_id: txnId,
    content: trimmedCaption || '',
    author: mxid,
    author_id: mxid,
    author_name: mxid,
    created_by: {
      id: mxid,
      full_name: mxid,
      user_name: mxid,
    },
    attachments: [{
      id: hashToNumber(eventId),
      file: upload.content_uri,
      mime_type: file.type,
      name: file.name,
      thumbnail: thumbnailMxc,
    }],
    created_at: new Date().toISOString(),
    is_read: false,
    is_delivered: true,
  }

  const enriched = await enrichMatrixMessage(matrixClient, message, room)
  if (!room) {
    return enriched
  }

  return enrichMessagesWithReadReceipts(matrixClient, room, [enriched])[0]
}

export async function sendMatrixMessagesWithMedia(
  roomId: string,
  files: File[],
  caption?: string,
  options?: { inReplyToEventId?: string },
): Promise<IMessage> {
  if (!files.length) {
    throw new Error('Нет файлов для отправки')
  }

  const matrixClient = await startMatrixClient()
  const trimmedCaption = caption?.trim()
  let lastMessage: IMessage | null = null

  for (let index = 0; index < files.length; index += 1) {
    const fileCaption = index === 0 ? trimmedCaption : undefined
    lastMessage = await sendMatrixMediaMessage(
      matrixClient,
      roomId,
      files[index],
      fileCaption,
      index === 0 ? options?.inReplyToEventId : undefined,
    )
  }

  return lastMessage!
}

export async function editMatrixMessage(
  roomId: string,
  matrixEventId: string,
  content: string,
): Promise<void> {
  await withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    const useFormatted = hasMarkdownMarkup(content)
    const newContent: Record<string, unknown> = {
      msgtype: MsgType.Text,
      body: content,
    }
    if (useFormatted) {
      newContent.format = 'org.matrix.custom.html'
      newContent.formatted_body = markdownToMatrixFormattedBody(content)
    }

    await matrixClient.sendEvent(roomId, EventType.RoomMessage, {
      'msgtype': MsgType.Text,
      'body': content,
      ...(useFormatted
        ? {
            format: 'org.matrix.custom.html',
            formatted_body: markdownToMatrixFormattedBody(content),
          }
        : {}),
      'm.new_content': newContent,
      'm.relates_to': {
        rel_type: RelationType.Replace,
        event_id: matrixEventId,
      },
    })
  })
}

export async function deleteMatrixMessage(
  roomId: string,
  matrixEventId: string,
): Promise<void> {
  await withMatrixAuthRetry(async () => {
    const matrixClient = await startMatrixClient()
    await matrixClient.redactEvent(roomId, matrixEventId)
  })
}
